/** Complete purchased-file screen, reused immediately after checkout and later from My Vault. */
import React, { useEffect, useState } from 'react';
import * as api from '../api';
import { BeatDetail, CheckoutResult } from '../types';
import { GhostBtn, s } from '../ui';
import { trackBeatDownloaded } from '../analytics';

const fmtBytes = (b: number | null): string => b == null ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

type DownloadBeat = Pick<BeatDetail, 'id' | 'name' | 'bpm' | 'musical_key' | 'files'>;

interface DownloadPanelProps {
  beat: DownloadBeat;
  /** Immediate checkout may need its order polled before an entitlement exists. */
  result?: CheckoutResult;
  /** Returning Vault visits already have the durable entitlement identifier. */
  entitlementId?: string;
  mode?: 'purchase' | 'vault';
  onBack: () => void;
}

export const DownloadPanel: React.FC<DownloadPanelProps> = ({
  beat, result, entitlementId: providedEntitlementId, mode = 'purchase', onBack,
}) => {
  const [format, setFormat] = useState<'wav' | 'mp3'>('wav');
  const [fmtMenu, setFmtMenu] = useState(false);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [entitlementId, setEntitlementId] = useState<string | null>(
    providedEntitlementId || result?.entitlementId || null
  );
  const [busyFile, setBusyFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (providedEntitlementId) {
      setEntitlementId(providedEntitlementId);
    } else if (!entitlementId && result?.orderId) {
      api.getOrder(result.orderId).then((o) => setEntitlementId(o.entitlement_id)).catch(() => {});
    }
  }, [entitlementId, providedEntitlementId, result?.orderId]);

  const downloadFile = async (fileKind: 'full_mix' | 'stems_zip' | 'stem', fileLabel?: string) => {
    if (!entitlementId) return;
    const key = `${fileKind}:${fileLabel || ''}`;
    setBusyFile(key);
    setError(null);
    try {
      await api.downloadEntitlementFile({
        entitlement_id: entitlementId,
        file_kind: fileKind,
        file_label: fileLabel,
        // Stems and their ZIP are always WAV-based; the selector only controls
        // the full mix.
        format: fileKind === 'full_mix' ? format : 'wav',
        filenameHint: `${beat.name.replace(/[^\w.-]+/g, '_')}${fileKind === 'stems_zip' ? '_stems.zip' : fileKind === 'stem' ? `_${(fileLabel || 'stem').replace(/[^\w.-]+/g, '_')}.wav` : `.${format}`}`,
      });
      trackBeatDownloaded(beat.id, fileKind);
    } catch (err: any) {
      setError(err.message || 'Could not prepare this download. Please try again.');
    } finally {
      setBusyFile(null);
    }
  };

  const makeMagicLink = async () => {
    setError(null);
    if (!entitlementId) return;
    try {
      const r = await api.mintDownload({ entitlement_id: entitlementId, file_kind: 'full_mix', format });
      const shareUrl = /^https?:\/\//i.test(r.token_url)
        ? r.token_url
        : `${(process.env.REACT_APP_BEATSHOP_API_BASE_URL || `${process.env.REACT_APP_API_BASE_URL || ''}/beatshop`).replace(/\/$/, '')}/v1/downloads/${r.token}`;
      setMagicLink(shareUrl);
      setCopied(false);
    } catch (err: any) {
      setError(err.message || 'Could not prepare this download link. Please try again.');
    }
  };

  const stems = beat.files.filter((f) => f.kind === 'stem');
  const zip = beat.files.find((f) => f.kind === 'stems_zip');
  const master = beat.files.find((f) => f.kind === (format === 'mp3' ? 'mp3' : 'master_wav'));

  const fileRows = [
    { key: 'full_mix:', name: `Full Mix — ${beat.name}`, meta: `${format === 'wav' ? 'WAV · 48kHz' : 'MP3 · 320 kbps'} · ${fmtBytes(master?.bytes ?? null)}`, iconBg: 'rgba(47,107,255,0.16)', iconStroke: 'var(--bs-blue-soft)', onGet: () => downloadFile('full_mix') },
    ...(zip ? [{ key: 'stems_zip:', name: 'All stems (zip)', meta: `ZIP · ${fmtBytes(zip.bytes)}`, iconBg: 'var(--bs-line)', iconStroke: 'var(--bs-text-mid)', onGet: () => downloadFile('stems_zip') }] : []),
    ...stems.map((st) => ({
      key: `stem:${st.label}`, name: st.label || 'Stem', meta: `WAV stem · ${fmtBytes(st.bytes)}`,
      iconBg: 'var(--bs-line)', iconStroke: 'var(--bs-text-mid)', onGet: () => downloadFile('stem', st.label || undefined),
    })),
  ];

  const chooseFormat = (next: 'wav' | 'mp3') => {
    setFormat(next);
    setFmtMenu(false);
    // A previously minted link points to the old format and is single-use.
    setMagicLink(null);
    setCopied(false);
  };

  return (
    <div style={s('max-width:740px;margin:0 auto;animation:bs-rise .3s ease both')}>
      <div style={s('display:flex;flex-direction:column;align-items:center;text-align:center;margin-bottom:28px')}>
        <div style={s('width:58px;height:58px;border-radius:50%;background:#2F6BFF;display:flex;align-items:center;justify-content:center;animation:bs-pop .4s ease both')}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5 5 11-12" /></svg>
        </div>
        <h2 style={s('margin:18px 0 0;font-weight:700;font-size:30px;letter-spacing:-0.02em')}>
          {mode === 'purchase' ? `${beat.name} is yours` : `${beat.name} — purchased files`}
        </h2>
        <p style={s('margin:10px 0 0;color:var(--bs-text-muted);font-size:14.5px;max-width:440px;line-height:1.55')}>
          {mode === 'purchase'
            ? "It's now removed from the public library — no one else can buy it. Find it anytime in your Vault."
            : stems.length
              ? 'Everything included with your purchase remains here: the full mix, all-stems ZIP, and every individual stem.'
              : 'Your complete purchase remains here, with the full mix available in WAV and MP3.'}
        </p>
      </div>

      <div style={s('background:linear-gradient(180deg,var(--bs-card-a),var(--bs-card-b));border:1px solid var(--bs-border);border-radius:18px;padding:20px 22px;margin-bottom:16px')}>
        <div style={s('font-size:11px;font-weight:600;letter-spacing:0.12em;color:var(--bs-text-label);margin-bottom:12px')}>YOUR MAGIC DOWNLOAD LINK</div>
        <div style={s('display:flex;gap:10px;align-items:center;flex-wrap:wrap')}>
          <div style={s('flex:1;min-width:230px;padding:12px 14px;background:var(--bs-input);border:1px solid var(--bs-border-2);border-radius:11px;font-size:13px;color:var(--bs-blue-soft);overflow:hidden;text-overflow:ellipsis;white-space:nowrap')}>
            {magicLink || 'Generate a single-use link to share the full mix'}
          </div>
          {magicLink ? (
            <button onClick={() => { navigator.clipboard.writeText(magicLink).catch(() => {}); setCopied(true); }}
              style={s('padding:12px 16px;background:var(--bs-chip);border:1px solid var(--bs-border-2);border-radius:11px;color:var(--bs-text-hi);font-weight:600;font-size:13px;cursor:pointer')}>
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          ) : (
            <button onClick={makeMagicLink} style={s('padding:12px 16px;background:var(--bs-chip);border:1px solid var(--bs-border-2);border-radius:11px;color:var(--bs-text-hi);font-weight:600;font-size:13px;cursor:pointer')}>Generate</button>
          )}
          {/* format dropdown */}
          <div style={s('position:relative')}>
            <button onClick={() => setFmtMenu(!fmtMenu)} style={s('display:flex;align-items:center;gap:7px;padding:12px 18px;background:var(--bs-btn-bg);color:var(--bs-btn-text);border:none;border-radius:11px;font-weight:600;font-size:13px;cursor:pointer;white-space:nowrap')}>
              Format · {format.toUpperCase()}
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="var(--bs-btn-text)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 4.5L6 8l3.5-3.5" /></svg>
            </button>
            {fmtMenu && (
              <div style={s('position:absolute;right:0;top:46px;z-index:30;width:210px;background:var(--bs-menu);border:1px solid var(--bs-border-2);border-radius:13px;padding:6px;box-shadow:0 16px 44px var(--bs-shadow-strong)')}>
                <div style={s('font-size:10px;font-weight:600;letter-spacing:0.1em;color:var(--bs-text-faint);padding:8px 9px 6px')}>CHOOSE FORMAT</div>
                {(['wav', 'mp3'] as const).map((f) => (
                  <button key={f} className="bs-fmt" onClick={() => chooseFormat(f)}
                    style={s('display:flex;align-items:center;justify-content:space-between;width:100%;text-align:left;padding:10px 11px;background:transparent;border:none;border-radius:9px;cursor:pointer')}>
                    <span style={s('display:flex;flex-direction:column;gap:1px')}>
                      <span style={s('font-weight:600;font-size:13.5px;color:var(--bs-text-hi)')}>{f.toUpperCase()}</span>
                      <span style={s('font-size:11px;color:var(--bs-text-label)')}>{f === 'wav' ? '48kHz · lossless' : '320 kbps'}</span>
                    </span>
                    {format === f && <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="var(--bs-blue-soft)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 7.5l3 3 6-7" /></svg>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={s('margin-top:12px;font-size:12px;color:var(--bs-text-dim)')}>Single-use &amp; short-lived — each link works exactly once; mint a fresh one anytime.</div>
      </div>

      <div style={s('background:linear-gradient(180deg,var(--bs-card-a),var(--bs-card-b));border:1px solid var(--bs-border);border-radius:18px;padding:6px 22px 12px')}>
        <div style={s('display:flex;align-items:center;justify-content:space-between;padding:15px 0 13px;border-bottom:1px solid var(--bs-border)')}>
          <span style={s('font-weight:600;font-size:14px;color:var(--bs-text-hi)')}>Files</span>
          <span style={s('font-size:12px;color:var(--bs-text-label)')}>{beat.bpm} BPM · {beat.musical_key}</span>
        </div>
        {fileRows.map((f) => (
          <div key={f.key} style={s('display:flex;align-items:center;gap:14px;padding:13px 0;border-bottom:1px solid var(--bs-line)')}>
            <div style={{ ...s('width:34px;height:34px;border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0'), background: f.iconBg }}>
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none" stroke={f.iconStroke} strokeWidth="1.5" strokeLinecap="round"><path d="M5 14V5l8-1.5V12" /><circle cx="4" cy="14" r="1.5" /><circle cx="13" cy="12" r="1.5" /></svg>
            </div>
            <div style={s('flex:1')}>
              <div style={s('font-weight:600;font-size:13.5px;color:var(--bs-text-hi)')}>{f.name}</div>
              <div style={s('font-size:11.5px;color:var(--bs-text-dim)')}>{f.meta}</div>
            </div>
            <button onClick={f.onGet} disabled={busyFile === f.key || !entitlementId || !!busyFile}
              style={s('padding:7px 13px;background:var(--bs-chip);border:1px solid var(--bs-border-2);border-radius:9px;color:var(--bs-text-body);font-weight:500;font-size:12.5px;cursor:pointer;' + (busyFile === f.key ? 'opacity:0.7;cursor:wait' : ''))}>
              {busyFile === f.key ? 'Downloading…' : 'Get'}
            </button>
          </div>
        ))}
      </div>

      {error && (
        <div style={s('margin-top:14px;padding:11px 13px;background:var(--bs-red-bg);border:1px solid var(--bs-red-border);border-radius:10px;color:var(--bs-red);font-size:13px')}>
          {error}
        </div>
      )}

      <div style={s('display:flex;justify-content:center;margin-top:26px')}>
        <GhostBtn onClick={onBack}>{mode === 'vault' ? 'Back to My Vault' : 'Back to Beat Shop'}</GhostBtn>
      </div>
    </div>
  );
};
